import React from 'react';
import _ from 'lodash';
import { useMatchStats } from '../../hooks/matchdetails';
import { useTeamLogo } from '../../hooks/team';
import { numberFormat } from '../../utils/numberFormat';

function MatchStats({ match, matchConfig }) {
  const { homeTeamName, visitingTeamName, homeTeamAbbrv, visitingTeamAbbrv } =
    match;
  const [matchStats] = useMatchStats(match?.id, match?.configSeasonStartYear);
  const [homeTeamLogo] = useTeamLogo(homeTeamAbbrv);
  const [visitingTeamLogo] = useTeamLogo(visitingTeamAbbrv);

  const showPossession = matchConfig?.hideStats?.ballPossession === false;
  const showDistance =
    matchConfig?.hideStats?.distance === false &&
    Object.values(matchConfig?.hidePlayerStats || {}).every((playerStats) => {
      return !playerStats.distance;
    });

  const [matchStatsSelection, setMatchStatsSelection] =
    React.useState('TOTALT');
  const matchStatsOptions = [
    {
      id: '1:A',
      label: '1:A HALVEK',
      shortLabel: '1:A',
    },
    {
      id: '2:A',
      label: '2:A HALVEK',
      shortLabel: '2:A',
    },
    {
      id: 'TOTALT',
      label: 'TOTALT',
      shortLabel: 'TOT',
    },
  ];

  const getStat = (stat) => (side, fixes) => {
    const firstPeriod =
      matchStats?.[
        `${side}${fixes?.home?.stat || stat}${
          fixes?.home?.prop || 'FirstPeriod'
        }`
      ] || 0;
    const secondPeriod =
      matchStats?.[
        `${side}${fixes?.visiting?.stat || stat}${
          fixes?.visiting?.prop || 'SecondPeriod'
        }`
      ] || 0;
    if (matchStatsSelection === '1:A') {
      return firstPeriod;
    }

    if (matchStatsSelection === '2:A') {
      return secondPeriod;
    }

    if (['Distance'].includes(stat)) {
      return firstPeriod + secondPeriod;
    }

    const suffix = fixes?.total?.prop || '';
    return (
      matchStats?.[`${side}${fixes?.total?.stat || ''}${suffix}`] ||
      matchStats?.[`${side}${stat}${suffix}`] ||
      0
    );
  };
  const getTeamDistance = getStat('Distance');
  const homeTeamDistance = getTeamDistance('homeTeam');
  const visitingTeamDistance = getTeamDistance('visitingTeam');

  const getTeamPossession = getStat('Possession');
  const homeTeamPossession = getTeamPossession('homeTeam', {
    total: {
      prop: 'Total',
    },
  });
  const visitingTeamPossession = getTeamPossession('visitingTeam', {
    total: {
      stat: 'Possesion',
      prop: 'Total',
    },
  });

  const getTeamShots = getStat('Shots');
  const homeTeamShots = getTeamShots('homeTeam');
  const visitingTeamShots = getTeamShots('visitingTeam');

  const getTeamShotsOnTarget = getStat('ShotsOnTarget');
  const homeTeamShotsOnTarget = getTeamShotsOnTarget('homeTeam');
  const visitingTeamShotsOnTarget = getTeamShotsOnTarget('visitingTeam');

  const getTeamYellowCards = getStat('YellowCards');
  const homeTeamYellowCards = getTeamYellowCards('homeTeam');
  const visitingTeamYellowCards = getTeamYellowCards('visitingTeam');

  const getTeamRedCards = getStat('RedCards');
  const homeTeamRedCards = getTeamRedCards('homeTeam');
  const visitingTeamRedCards = getTeamRedCards('visitingTeam');

  // API doesn't support them yet
  const getTeamCorners = getStat('Corners');
  const homeTeamCorners = getTeamCorners('homeTeam');
  const visitingTeamCorners = getTeamCorners('visitingTeam');

  return (
    <section className="col-12 mt-5">
      <div className="col-12">
        <div className="row pb-2">
          <div className="col-6 d-flex ">
            <h5 className="heading-h5 d-lg-block">Matchstatistik</h5>
          </div>
          {matchStats ? (
            <div className="col-6 d-flex align-items-center justify-content-end gap-3">
              <div className="tabs">
                <div className="row">
                  {matchStatsOptions.map((button) => (
                    <div className="col-4">
                      <div
                        style={{
                          whiteSpace: 'nowrap',
                        }}
                        className={`tabs__item tabs__item-switch text-uppercase ${
                          button.id === matchStatsSelection
                            ? 'tabs__item--is-active'
                            : ''
                        }`}
                        onClick={() => {
                          setMatchStatsSelection(button.id);
                        }}
                        aria-hidden="true"
                      >
                        <span className="d-none d-md-block">
                          {button.label}
                        </span>
                        <span className="d-block d-md-none">
                          {button.shortLabel}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div className="data-container">
          <div className="data-container__row data-container__header px-2 px-lg-3 py-2">
            <div className="row">
              <div className="col-6 col-lg-4 d-flex align-items-center gap-3">
                <img height="35" src={homeTeamLogo} alt="" />
                <span className="d-none d-lg-block heading-h5 text-transform-none">
                  {homeTeamName}
                </span>
              </div>
              <div className="col d-none d-lg-flex align-items-center justify-content-center heading-h5 text-transform-none gap-2 gap-lg-4">
                Totalt
              </div>
              <div className="col-6 col-lg-4 d-flex align-items-center justify-content-end gap-3">
                <span className="d-none d-lg-block heading-h5 text-transform-none">
                  {visitingTeamName}
                </span>
                <img height="35" src={visitingTeamLogo} alt="" />
              </div>
            </div>
          </div>
          {showPossession &&
            homeTeamPossession > 0 &&
            visitingTeamPossession > 0 && (
              <div className="row mx-0 px-2 px-lg-3 py-2 data-container__row">
                <div className="col-4 col-lg-5 d-flex align-items-center text-small text-lg-regular fw-bold pe-0">
                  <div>{homeTeamPossession}%</div>
                  <div
                    className="progress ms-auto"
                    style={{ width: `${homeTeamPossession}%` }}
                  />
                </div>
                <div className="col d-flex align-items-center justify-content-center gap-3 gap-lg-4 text-small text-lg-regular fw-bold px-0">
                  Bollinnehav %
                </div>
                <div className="col-4 col-lg-5 d-flex align-items-center justify-content-end text-small text-lg-regular fw-bold ps-0">
                  <div
                    className="progress"
                    style={{
                      width: `${visitingTeamPossession}%`,
                    }}
                  />
                  <div className="ms-auto">{visitingTeamPossession}%</div>
                </div>
              </div>
            )}
          <div className="row mx-0 px-2 px-lg-3 py-2 data-container__row">
            <div className="col-4 col-lg-5 d-flex align-items-center text-small text-lg-regular fw-bold pe-0">
              {homeTeamShots}
            </div>
            <div className="col d-flex align-items-center justify-content-center gap-3 gap-lg-4 text-small text-lg-regular fw-bold px-0">
              Avslut
            </div>
            <div className="col-4 col-lg-5 d-flex align-items-center justify-content-end text-small text-lg-regular fw-bold ps-0">
              {visitingTeamShots}
            </div>
          </div>
          <div className="row mx-0 px-2 px-lg-3 py-2 data-container__row">
            <div className="col-4 col-lg-5 d-flex align-items-center text-small text-lg-regular fw-bold pe-0">
              {homeTeamShotsOnTarget}
            </div>
            <div className="col d-flex align-items-center justify-content-center gap-3 gap-lg-4 text-small text-lg-regular fw-bold px-0">
              Avslut på mål
            </div>
            <div className="col-4 col-lg-5 d-flex align-items-center justify-content-end text-small text-lg-regular fw-bold ps-0">
              {visitingTeamShotsOnTarget}
            </div>
          </div>
          <div className="row mx-0 px-2 px-lg-3 py-2 data-container__row">
            <div className="col-4 col-lg-5 d-flex align-items-center text-small text-lg-regular fw-bold pe-0">
              {homeTeamYellowCards}
            </div>
            <div className="col d-flex align-items-center justify-content-center gap-3 gap-lg-4 text-small text-lg-regular fw-bold px-0">
              Gula kort
            </div>
            <div className="col-4 col-lg-5 d-flex align-items-center justify-content-end text-small text-lg-regular fw-bold ps-0">
              {visitingTeamYellowCards}
            </div>
          </div>
          <div className="row mx-0 px-2 px-lg-3 py-2 data-container__row">
            <div className="col-4 col-lg-5 d-flex align-items-center text-small text-lg-regular fw-bold pe-0">
              {homeTeamRedCards}
            </div>
            <div className="col d-flex align-items-center justify-content-center gap-3 gap-lg-4 text-small text-lg-regular fw-bold px-0">
              Röda kort
            </div>
            <div className="col-4 col-lg-5 d-flex align-items-center justify-content-end text-small text-lg-regular fw-bold ps-0">
              {visitingTeamRedCards}
            </div>
          </div>
          {showDistance &&
            matchStats?.homeTeamDistanceFirstPeriod > 0 &&
            matchStats?.visitingTeamDistanceFirstPeriod > 0 && (
              <div className="row mx-0 px-2 px-lg-3 py-2 data-container__row">
                <div className="col-4 col-lg-5 d-flex align-items-center text-small text-lg-regular fw-bold pe-0">
                  {numberFormat(_.round(homeTeamDistance, 0))}
                </div>
                <div className="col d-flex align-items-center justify-content-center gap-3 gap-lg-4 text-small text-lg-regular fw-bold px-0">
                  Löpmeter (m)
                </div>
                <div className="col-4 col-lg-5 d-flex align-items-center justify-content-end text-small text-lg-regular fw-bold ps-0">
                  {numberFormat(_.round(visitingTeamDistance, 0))}
                </div>
              </div>
            )}
          {matchStatsSelection === 'TOTALT' ? (
            <div className="row mx-0 px-2 px-lg-3 py-2 data-container__row">
              <div className="col-4 col-lg-5 d-flex align-items-center text-small text-lg-regular fw-bold pe-0">
                {homeTeamCorners}
              </div>
              <div className="col d-flex align-items-center justify-content-center gap-3 gap-lg-4 text-small text-lg-regular fw-bold px-0">
                Hörnor
              </div>
              <div className="col-4 col-lg-5 d-flex align-items-center justify-content-end text-small text-lg-regular fw-bold ps-0">
                {visitingTeamCorners}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default MatchStats;
